import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, Navigate, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Settings as SettingsIcon,
  CreditCard,
  Loader2,
  CheckCircle2,
  AlertCircle,
  User,
  LogOut,
  LifeBuoy,
  BookOpen,
  Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogoText } from '../components/ui/Logo';
import { EmailPreferences } from '../components/EmailPreferences';
import { UsageDisplay } from '../components/UsageDisplay';
import {
  billingService,
  type BillingTierConfigRow,
  type Subscription,
} from '../services/billing';
import { useAuth } from '../context/AuthContext';
import { useBusinessAnalytics } from '../utils/analytics';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../utils/errorUtils';
import { cn } from '@/lib/utils';
import AchievementsGallery from '../components/AchievementsGallery';
import ActivityTimeline from '../components/ActivityTimeline';
import UserLevelProgress from '../components/UserLevelProgress';
import {
  SETTINGS_NAV_GROUPS,
  SETTINGS_SECTIONS,
  flattenSettingsNavItems,
  settingsNavSectionLabel,
  settingsPath,
  type SettingsNavSegment,
} from '../settings/settingsNavConfig';

const SETTINGS_PATH_PREFIX = `${'/settings'}/`;
const URL_PATH_SEPARATOR = '/';

function parseSettingsSection(
  pathname: string
): SettingsNavSegment | 'root' | 'invalid' | 'legacyEmailPreferences' {
  if (pathname === '/settings') return 'root';
  if (!pathname.startsWith(SETTINGS_PATH_PREFIX)) return 'invalid';
  const segment =
    pathname.slice(SETTINGS_PATH_PREFIX.length).split(URL_PATH_SEPARATOR)[0] ?? '';
  if (!segment) return 'invalid';
  if (segment === 'email-preferences') return 'legacyEmailPreferences';
  if (!SETTINGS_SECTIONS.has(segment)) return 'invalid';
  return segment as SettingsNavSegment;
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('settings');
  const { t: commonT } = useTranslation('common');
  const { t: authT } = useTranslation('auth');
  const { user, isAuthenticated, isLoading: authLoading, logout, refreshSession } = useAuth();
  const { trackFeatureUsed } = useBusinessAnalytics();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const jwtTierSyncRef = useRef<string>('');
  const stripeCustomerHealRef = useRef(false);
  const [tierConfigs, setTierConfigs] = useState<BillingTierConfigRow[]>([]);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  const [displayName, setDisplayName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!user) return;
    const name =
      (typeof user.user_metadata?.full_name === 'string' && user.user_metadata.full_name) ||
      (typeof user.user_metadata?.name === 'string' && user.user_metadata.name) ||
      '';
    setDisplayName(name);
  }, [user, user?.id, user?.user_metadata?.full_name, user?.user_metadata?.name]);

  useEffect(() => {
    jwtTierSyncRef.current = '';
    stripeCustomerHealRef.current = false;
  }, [user?.id]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [sub, tierList] = await Promise.all([
          billingService.getCurrentSubscription(),
          billingService.getOrderedTierConfigs(),
        ]);
        if (!cancelled) {
          setSubscription(sub);
          setTierConfigs(tierList);
        }
      } catch (err) {
        console.error('Error loading subscription or tiers:', err);
      } finally {
        if (!cancelled) {
          setLoadingSubscription(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (loadingSubscription || !subscription?.tier || !user) return;
    const jwtTier = (user.user_metadata?.tier as string) || 'free';
    if (subscription.tier === jwtTier) return;
    const syncKey = `${user.id}:${subscription.tier}`;
    if (jwtTierSyncRef.current === syncKey) return;
    jwtTierSyncRef.current = syncKey;
    void refreshSession().catch(() => {});
  }, [
    loadingSubscription,
    subscription?.tier,
    user?.id,
    user?.user_metadata?.tier,
    refreshSession,
    user,
  ]);

  useEffect(() => {
    if (loadingSubscription || !isAuthenticated || !user || stripeCustomerHealRef.current) {
      return;
    }
    const cid = user.user_metadata?.stripe_customer_id;
    if (!cid || typeof cid !== 'string') return;
    if ((subscription?.tier || 'free') !== 'free') return;

    stripeCustomerHealRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const result = await billingService.syncSubscriptionFromStripe();
        if (cancelled) return;
        if (!result.synced) {
          stripeCustomerHealRef.current = false;
          return;
        }
        const [sub, tierList] = await Promise.all([
          billingService.getCurrentSubscription(),
          billingService.getOrderedTierConfigs(),
        ]);
        if (!cancelled) {
          setSubscription(sub);
          setTierConfigs(tierList);
          await refreshSession();
        }
      } catch (e) {
        console.error('Stripe customer tier heal failed:', e);
        stripeCustomerHealRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    loadingSubscription,
    isAuthenticated,
    user,
    subscription?.tier,
    refreshSession,
  ]);

  const handleManageSubscription = async () => {
    setLoading(true);
    setError(null);

    try {
      trackFeatureUsed('customer_portal_opened', {
        source: 'settings_page',
        current_tier:
          subscription?.tier || (user?.user_metadata?.tier as string) || 'free',
      });

      const response = await billingService.createCustomerPortalSession();
      window.location.href = response.url;
    } catch (err: unknown) {
      console.error('Error creating portal session:', err);
      const message = err instanceof Error ? err.message : '';
      if (message.includes('No active subscription')) {
        setError(t('errors.noActiveSubscription'));
      } else {
        setError(t('errors.portalFailed'));
      }
      setLoading(false);
    }
  };

  const handleSaveDisplayName = async () => {
    if (!user) return;
    const trimmed = displayName.trim();
    setSavingProfile(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          full_name: trimmed,
          name: trimmed,
        },
      });
      if (updateError) {
        throw updateError;
      }
      await refreshSession();
      toast.success(t('account.profileSaved'));
    } catch (err: unknown) {
      console.error('Failed to update display name:', err);
      toast.error(
        extractErrorMessage(err, t('account.profileSaveFailed'))
      );
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  const initialDisplayName = useMemo(() => {
    if (!user) return '';
    return (
      (typeof user.user_metadata?.full_name === 'string' && user.user_metadata.full_name) ||
      (typeof user.user_metadata?.name === 'string' && user.user_metadata.name) ||
      ''
    );
  }, [user]);

  const profileDirty =
    user != null && displayName.trim() !== (typeof initialDisplayName === 'string' ? initialDisplayName.trim() : '');

  const providerLabel =
    user?.app_metadata?.provider === 'google'
      ? t('account.provider.google')
      : t('account.provider.email');

  const effectiveTier =
    (subscription?.tier as string) ||
    (user?.user_metadata?.tier as string) ||
    'free';
  const isPaidTier = effectiveTier !== 'free';
  const currentTierConfig = tierConfigs.find((c) => c.tier === effectiveTier);

  const planPriceLine = useMemo(() => {
    if (!currentTierConfig) return null;
    if (effectiveTier === 'enterprise' || currentTierConfig.price_monthly == null) {
      return t('planPrice.custom');
    }
    if (currentTierConfig.price_monthly === 0) {
      return t('planPrice.free');
    }
    const formatted = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(currentTierConfig.price_monthly);
    return t('planPrice.perMonth', { price: formatted });
  }, [effectiveTier, currentTierConfig, t]);

  const subscriptionStatusLabelKey = useMemo(() => {
    const raw = (subscription?.status || 'none').toLowerCase();
    if (raw === 'none') return 'status.none';
    if (['not_subscribed', 'canceled', 'incomplete_expired'].includes(raw)) {
      return 'status.notSubscribed';
    }
    if (raw === 'active') return 'status.active';
    if (raw === 'trialing') return 'status.trialing';
    if (raw === 'past_due') return 'status.pastDue';
    if (raw === 'incomplete') return 'status.incomplete';
    if (raw === 'unpaid') return 'status.unpaid';
    if (raw === 'paused') return 'status.paused';
    if (raw === 'unknown') return 'status.unknown';
    return 'status.unknown';
  }, [subscription?.status]);

  const subscriptionStatusVisual = useMemo(() => {
    const raw = (subscription?.status || 'none').toLowerCase();
    if (['active', 'trialing'].includes(raw)) return 'success';
    if (['past_due', 'unpaid', 'incomplete', 'paused'].includes(raw)) return 'warning';
    return 'muted';
  }, [subscription?.status]);

  const tierBadge = useMemo(() => {
    const label = currentTierConfig?.display_name ?? effectiveTier;
    switch (effectiveTier) {
      case 'free':
        return <Badge variant="secondary">{label}</Badge>;
      case 'starter':
        return <Badge className="bg-blue-500 text-white">{label}</Badge>;
      case 'pro':
        return <Badge className="bg-purple-500 text-white">{label}</Badge>;
      case 'enterprise':
        return (
          <Badge className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
            {label}
          </Badge>
        );
      default:
        return <Badge variant="secondary">{label}</Badge>;
    }
  }, [effectiveTier, currentTierConfig]);

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="animate-spin h-8 w-8 text-primary mx-auto" />
          <p className="text-muted-foreground">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const routeState = parseSettingsSection(location.pathname);
  if (routeState === 'root') {
    return <Navigate to="/settings/profile" replace />;
  }
  if (routeState === 'legacyEmailPreferences') {
    return <Navigate to="/settings/notifications" replace />;
  }
  if (routeState === 'invalid') {
    return <Navigate to="/settings/profile" replace />;
  }
  const section = routeState;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex justify-between h-16 items-center">
            <LogoText variant="dark" size="md" iconSize={32} className="text-foreground" />
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('backToDashboard')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="mx-auto max-w-5xl space-y-6">
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-3 rounded-full bg-indigo-100">
              <SettingsIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{t('pageTitle')}</h1>
              <p className="text-muted-foreground">{t('pageSubtitle')}</p>
            </div>
          </div>

          <div className="flex flex-col gap-6 md:flex-row md:items-start">
            <div className="space-y-2 md:hidden">
              <Label htmlFor="settings-section-select">{t('nav.mobileSectionLabel')}</Label>
              <select
                id="settings-section-select"
                className={cn(
                  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
                  'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                )}
                value={location.pathname}
                onChange={(e) => {
                  navigate(e.target.value);
                }}
              >
                {flattenSettingsNavItems().map(({ segment }) => (
                  <option key={segment} value={settingsPath(segment)}>
                    {settingsNavSectionLabel(t, segment)}
                  </option>
                ))}
              </select>
            </div>

            <nav
              className="hidden shrink-0 space-y-6 md:block md:w-56"
              aria-label={t('nav.ariaLabel')}
            >
              {SETTINGS_NAV_GROUPS.map((group) => (
                <div key={group.groupLabelKey}>
                  <p className="mb-2 px-3 text-xs font-semibold text-muted-foreground">
                    {t(group.groupLabelKey)}
                  </p>
                  <div className="space-y-1">
                    {group.items.map(({ segment, icon: Icon }) => (
                      <NavLink
                        key={segment}
                        to={settingsPath(segment)}
                        end
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ring-offset-background transition-colors',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                            isActive
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                          )
                        }
                      >
                        <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                        {settingsNavSectionLabel(t, segment)}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            <div
              className={cn(
                'min-w-0 flex-1 space-y-6',
                section === 'progress' ? 'max-w-4xl' : 'max-w-2xl'
              )}
            >
          {section === 'profile' && (
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <User className="w-5 h-5 text-muted-foreground" />
                  <CardTitle>{t('account.title')}</CardTitle>
                </div>
                <CardDescription>{t('account.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="settings-email">{t('account.emailLabel')}</Label>
                  <Input
                    id="settings-email"
                    type="email"
                    value={user?.email ?? ''}
                    readOnly
                    disabled
                    className="bg-muted/50"
                  />
                  <p className="text-xs text-muted-foreground">{t('account.emailReadOnlyHint')}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">{t('account.providerLabel')}</p>
                  <Badge variant="outline">{providerLabel}</Badge>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="settings-display-name">{t('account.displayNameLabel')}</Label>
                  <Input
                    id="settings-display-name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={t('account.displayNamePlaceholder')}
                    autoComplete="name"
                  />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    type="button"
                    onClick={() => void handleSaveDisplayName()}
                    disabled={savingProfile || !profileDirty}
                  >
                    {savingProfile ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('account.savingProfile')}
                      </>
                    ) : (
                      t('account.saveProfile')
                    )}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => void handleSignOut()}>
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('account.signOut')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {section === 'plan' && (
            <>
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <div className="px-1">
                  <h2 className="text-lg font-semibold text-foreground">{t('usage.sectionTitle')}</h2>
                  <p className="text-sm text-muted-foreground">{t('usage.sectionDescription')}</p>
                </div>
                <UsageDisplay compact showTimeframes={false} showUpgradeButton />
              </div>

              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <CreditCard className="w-5 h-5 text-muted-foreground" />
                    <CardTitle>{t('billing.title')}</CardTitle>
                  </div>
                  <CardDescription>{t('billing.description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between gap-4">
                      <div className="space-y-2 min-w-0">
                        <p className="text-sm font-medium text-muted-foreground">{t('currentPlan')}</p>
                        <div className="flex flex-wrap items-center gap-2">{tierBadge}</div>
                        {planPriceLine && (
                          <p className="text-sm text-muted-foreground">{planPriceLine}</p>
                        )}
                      </div>
                      {loadingSubscription ? (
                        <Loader2 className="animate-spin h-5 w-5 text-muted-foreground shrink-0" />
                      ) : (
                        <div className="text-right shrink-0">
                          <div className="flex items-center justify-end space-x-1 text-sm text-muted-foreground">
                            {subscriptionStatusVisual === 'success' ? (
                              <>
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                <span>{t(subscriptionStatusLabelKey)}</span>
                              </>
                            ) : subscriptionStatusVisual === 'warning' ? (
                              <>
                                <AlertCircle className="w-4 h-4 text-amber-500" />
                                <span>{t(subscriptionStatusLabelKey)}</span>
                              </>
                            ) : (
                              <span>{t(subscriptionStatusLabelKey)}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {isPaidTier ? (
                    <>
                      <Button
                        onClick={handleManageSubscription}
                        disabled={loading}
                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                        size="lg"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('opening')}
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            {t('manageSubscription')}
                          </>
                        )}
                      </Button>
                      <p className="text-sm text-muted-foreground">{t('stripeRedirectNote')}</p>
                    </>
                  ) : (
                    <div className="text-center py-8 space-y-4">
                      <p className="text-muted-foreground">{t('freePlanCta')}</p>
                      <Button
                        onClick={() => navigate('/billing/upgrade')}
                        className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                      >
                        {t('viewPricing')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {section === 'notifications' && (
            <div className="space-y-2">
              <div className="px-1">
                <h2 className="text-lg font-semibold text-foreground">
                  {t('emailNotifications.sectionTitle')}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t('emailNotifications.sectionDescription')}
                </p>
              </div>
              <EmailPreferences />
            </div>
          )}

          {section === 'progress' && (
            <div className="space-y-8">
              <div className="px-1">
                <h2 className="text-lg font-semibold text-foreground">{t('progress.sectionTitle')}</h2>
                <p className="text-sm text-muted-foreground">{t('progress.sectionDescription')}</p>
              </div>
              <AchievementsGallery activityFeedScope="account" />
              <UserLevelProgress activityFeedScope="account" />
              <ActivityTimeline limit={20} activityFeedScope="account" />
            </div>
          )}

          {section === 'help' && (
            <>
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <LifeBuoy className="w-5 h-5 text-muted-foreground" />
                    <CardTitle>{t('help.title')}</CardTitle>
                  </div>
                  <CardDescription>{t('help.description')}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col sm:flex-row flex-wrap gap-3">
                  <Button variant="outline" className="justify-center" asChild>
                    <Link to="/support">
                      <LifeBuoy className="w-4 h-4 mr-2" />
                      {t('help.support')}
                    </Link>
                  </Button>
                  <Button variant="outline" className="justify-center" asChild>
                    <Link to="/docs">
                      <BookOpen className="w-4 h-4 mr-2" />
                      {t('help.docs')}
                    </Link>
                  </Button>
                  <Button variant="outline" className="justify-center" asChild>
                    <Link to="/billing/upgrade">
                      <Tag className="w-4 h-4 mr-2" />
                      {t('help.plans')}
                    </Link>
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{commonT('legal_section_title')}</CardTitle>
                  <CardDescription>{commonT('legal_section_description')}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col sm:flex-row gap-4">
                  <Button variant="outline" className="justify-center" asChild>
                    <Link to="/legal/privacy-policy">{authT('privacyPolicy')}</Link>
                  </Button>
                  <Button variant="outline" className="justify-center" asChild>
                    <Link to="/legal/terms">{authT('termsOfService')}</Link>
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
